/** The "why is this project where it is?" modal, opened from a roadmap row.
 *
 * The roadmap answers WHERE a project is; this answers WHY — the sub tasks with their
 * states, and the people on the hook. Deliberately read-only: the roadmap is a wall
 * view for planning sessions, and editing belongs on the Workbench and project pages
 * where the guards (concurrency, permissions, reasons) already live.
 */

import { Pill } from '@/components/ui'
import { formatDate, ragTone, stageLabel } from '@/lib/domain'
import { useAssignments } from '@/features/delivery/useDelivery'
import { useWorkItems } from '@/features/workbench/useWork'

const STATUS_LABEL: Record<string, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
}

const STATUS_TONE = {
  todo: 'neutral',
  in_progress: 'info',
  blocked: 'red',
  done: 'green',
} as const

const ROLE_LABEL: Record<string, string> = {
  lead: 'Lead',
  contributor: 'Contributor',
  stakeholder: 'Stakeholder',
}

export interface DrillDownProject {
  id: string
  name: string
  stage: string
  ragStatus: string
  percentComplete: number
  ownerName: string
  startDate?: string | null
  targetDate?: string | null
}

export function ProjectDrillDown({
  project,
  onClose,
}: {
  project: DrillDownProject
  onClose: () => void
}) {
  const { data: itemData } = useWorkItems({ projectId: project.id })
  const { data: assignments } = useAssignments(project.id)

  const items = itemData?.items ?? []
  // Unfinished work first — it is the answer to "why is this behind?". Blocked work
  // ahead of everything, because a blocked task is the sentence someone came to read.
  const order = { blocked: 0, in_progress: 1, todo: 2, done: 3 } as const
  const sorted = [...items].sort(
    (a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9),
  )
  const openCount = items.filter((item) => item.status !== 'done').length

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={project.name}
      >
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h2 className="font-heading text-2xl text-fg">{project.name}</h2>
          {project.ragStatus !== 'none' ? (
            <Pill tone={ragTone(project.ragStatus)}>
              {project.ragStatus === 'green'
                ? 'On track'
                : project.ragStatus === 'amber'
                  ? 'At risk'
                  : 'Behind'}
            </Pill>
          ) : null}
        </div>
        <p className="mb-4 text-sm text-fg-muted">
          {stageLabel(project.stage)} · {project.percentComplete}% complete
          {project.ownerName ? ` · ${project.ownerName}` : ''}
          {project.targetDate ? ` · target ${formatDate(project.targetDate)}` : ''}
        </p>

        <h3 className="section-rule mb-2 text-fg">
          Work items{items.length ? ` — ${openCount} open of ${items.length}` : ''}
        </h3>
        {sorted.length === 0 ? (
          <p className="mb-4 text-sm text-fg-muted">
            No work items yet — the percentage above is not backed by tracked work.
          </p>
        ) : (
          <ul className="card mb-4 divide-y divide-border">
            {sorted.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-3 py-2">
                <Pill tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Pill>
                <span className="min-w-0 flex-1 truncate text-sm text-fg" title={item.title}>
                  {item.title}
                </span>
                {item.points !== null ? (
                  <span className="micro-label text-fg-muted">{item.points} pts</span>
                ) : null}
                <span className="text-xs text-fg-muted">{item.assigneeName || 'Unassigned'}</span>
              </li>
            ))}
          </ul>
        )}

        <h3 className="section-rule mb-2 text-fg">Resources</h3>
        {(assignments ?? []).length === 0 ? (
          <p className="text-sm text-fg-muted">
            Nobody is assigned to this project — which may itself be the answer.
          </p>
        ) : (
          <ul className="card divide-y divide-border">
            {(assignments ?? []).map((assignment) => (
              <li key={assignment.id} className="flex items-center justify-between px-3 py-2">
                <span className="text-sm text-fg">{assignment.displayName}</span>
                <span className="text-xs text-fg-muted">
                  {ROLE_LABEL[assignment.role] ?? assignment.role}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
